import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import TicketService from "../../api/ticketService";
import WishService from "../../api/wishService";
import {IWinnerTicket, IWish, WishStatus, WishType} from "../../store/wish/Helper";
import {removeWishFromWishlist} from "../../store/wishlist/Slice";
import {removeWish} from "../../store/wish/Slice";
import {parseSkopjeDateMs} from "../skopjeTime";
import {hasCompleteWinners} from "../winnerStatus";

export interface WinnerEntry {
    id: string;
    code: string;
    full_name: string;
    recipient_name?: string;
    profile_picture?: string;
}

const POLL_INTERVAL = 15000; // 15 seconds in ms
const DRAW_DURATION = 180000; // 3 minutes in ms
const POLL_START_DELAY = 75000; // 1.5 minutes in ms

const useWishCountdown = (wish: IWish) => {
    const [countDown, setCountDown] = useState<number | null>(0);
    const [drawStarted, setDrawStarted] = useState(false);
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);
    const [ticketsArray, setTicketsArray] = useState<WinnerEntry[]>([]);
    const [winners, setWinners] = useState<IWinnerTicket[]>([]);
    const [fetchedWish, setFetchedWish] = useState<IWish | null>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const initialFetch = useRef(false)
    const dispatch = useAppDispatch()
    const isVoucher = useMemo(() => wish.type === WishType.VOUCHER, [wish.type])
    const removesOnExpiry = useMemo(() => (
        wish.type === WishType.VOUCHER || wish.type === WishType.BUNDLE
    ), [wish.type])
    const wishEndMs = useMemo(() => parseSkopjeDateMs(wish.end_time), [wish.end_time]);
    const latestWish = useMemo(() => {
        const fetchedCurrentWish = fetchedWish?.id === wish.id ? fetchedWish : null;
        const nextWish = fetchedCurrentWish ? {...wish, ...fetchedCurrentWish} : wish;

        return {
            ...nextWish,
            status: wish.status === WishStatus.FINISHED
                ? WishStatus.FINISHED
                : nextWish.status,
        };
    }, [fetchedWish, wish]);
    const winnersComplete = useMemo(
        () => hasCompleteWinners(latestWish, winners),
        [latestWish, winners]
    );
    const shouldPollForWinners = useMemo(() => (
        latestWish.status === WishStatus.ACTIVE ||
        (latestWish.status === WishStatus.FINISHED && !winnersComplete)
    ), [latestWish.status, winnersComplete]);

    const fetchWishDetails = useCallback(() => {
        if (removesOnExpiry) {
            dispatch(removeWishFromWishlist(wish.id))
            dispatch(removeWish(wish.id))
            return
        }
        WishService.fetchWishDetails(wish.id)
            .then(({data}: { data: IWish }) => {
                setFetchedWish(data);
                setWinners(data.winners ?? []);
            })
            .catch(err => console.error("Error fetching wish details", err));
    }, [wish.id, removesOnExpiry, dispatch])

    // Calculate the countdown.
    const calculateCountdown = useCallback(() => {
        if (Number.isFinite(wishEndMs) && serverTime !== null) {
            const elapsed = serverTime - wishEndMs;

            if (elapsed < 0) {
                // Before the draw: show time until start.
                setCountDown(-elapsed);
            } else if (removesOnExpiry) {
                // Vouchers and bundles finish immediately when their sales window ends.
                setCountDown(null)
                setTicketsArray((prev) => (prev.length ? [] : prev))
                setDrawStarted(false)
            } else if (elapsed < DRAW_DURATION) {
                // Draw in progress: mark as started and show remaining time.
                setDrawStarted(true);
                setCountDown(DRAW_DURATION - elapsed);
            } else if (winnersComplete) {
                // Winners are fully announced: clear countdown and animation tickets.
                setCountDown(null)
                setTicketsArray((prev) => (prev.length ? [] : prev))
                setDrawStarted(false)
            } else {
                // Keep the winner picker animation alive while the finish job is still pending.
                setCountDown(null)
                setDrawStarted(true)
            }
        }
    }, [removesOnExpiry, serverTime, wishEndMs, winnersComplete]);

    useEffect(() => {
        calculateCountdown();
    }, [calculateCountdown]);

    useEffect(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        initialFetch.current = false;
        setCountDown(0);
        setDrawStarted(false);
        setTicketsArray([]);
        setWinners([]);
        setFetchedWish(null);
    }, [wish.id]);

    const fetchDrawTickets = useCallback(() => {
        if (isVoucher) return;
        TicketService.fetchTicketsForWish(wish.id)
            .then(res => {
                setTicketsArray(res.data);
            })
            .catch(err => console.error("Error fetching tickets", err));
    }, [wish.id, isVoucher]);

    // Fetch tickets once the draw has started.
    useEffect(() => {
        if (drawStarted) {
            fetchDrawTickets();
        }
    }, [drawStarted, fetchDrawTickets]);

    // Start polling when the draw is in progress and after the POLL_START_DELAY has passed.
    useEffect(() => {
        if (winnersComplete || !shouldPollForWinners) return;

        if (removesOnExpiry && drawStarted && !initialFetch.current) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        if (!initialFetch.current && countDown === null) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        // Start polling when the draw is in progress and the countdown has reached the poll delay.
        if (drawStarted && (countDown === null || countDown <= (DRAW_DURATION - POLL_START_DELAY)) && !pollIntervalRef.current) {
            // Trigger immediate fetch once when countDown is null and it hasn't been fetched yet.
            if (!initialFetch.current) {
                fetchWishDetails();
                initialFetch.current = true;
            }
            pollIntervalRef.current = setInterval(fetchWishDetails, POLL_INTERVAL);
        }
    }, [drawStarted, winnersComplete, countDown, fetchWishDetails, shouldPollForWinners, removesOnExpiry]);

    // Clear polling once winners are complete or the wish is no longer waiting for winner data.
    useEffect(() => {
        if ((winnersComplete || !shouldPollForWinners) && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [winnersComplete, shouldPollForWinners]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    return {countDown, drawStarted, setDrawStarted, ticketsArray, setTicketsArray, winners, latestWish};
};

export default useWishCountdown;
